import React, { useState, useEffect } from "react";
import Meta from "../components/Meta";
import PageLoader from "../components/PageLoader";
import { useRouter } from "../util/router";
import { useAuth } from "../util/auth";
import { getStripePriceId, getOnboardingRoute } from "../util/prices";
import { apiRequestNoAuth, apiRequest } from "../util/util";
import LeadCaptureForm from "../components/checkout/LeadCaptureForm";
import CheckoutSummary from "../components/checkout/CheckoutSummary";
import BillingSelector from "../components/checkout/BillingSelector";
import { getProductFamily } from "../components/PricingSectionCommon";
import logo from "../assets/logo.png";

function CheckoutPage() {
  const router = useRouter();
  const auth = useAuth();

  const [leadData, setLeadData] = useState(null);
  const [formComplete, setFormComplete] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);

  // Parse URL params
  // src: traffic source tag (e.g. ?src=sandb from stairsandbuckets.org). Passed through
  // to Stripe session metadata so the webhook can attribute the sale after the redirect.
  // Clamped because it's user-controlled and Stripe rejects metadata values over 500
  // chars — a mangled link must degrade to a truncated tag, not a failed checkout.
  // String() flattens the array that duplicate ?src= params parse into.
  const { plan, product, src: rawSrc, coupon: rawCoupon } = router.query;
  const src = rawSrc ? String(rawSrc).slice(0, 100) : undefined;

  // coupon: /purchase/:plan?coupon=X redirects here with ?coupon=X
  // (src/pages/purchase.js), and this page never read it, so every coupon
  // link silently charged full price. Clamped and String()-flattened for the
  // same reasons as src above. It is passed to the checkout route as
  // `couponCode`, which resolves it against active promotion codes only and
  // drops anything that does not match -- this value is public and
  // user-controlled, so it must never reach Stripe as a raw coupon id.
  const coupon = rawCoupon ? String(rawCoupon).slice(0, 100) : undefined;

  // Determine if we're using product family mode or direct plan mode
  const productFamily = product ? getProductFamily(product) : null;
  const isProductMode = !!productFamily;

  // Initialize selected plan when product family is available
  useEffect(() => {
    if (productFamily && !selectedPlan) {
      // Default to annual (best value)
      setSelectedPlan(productFamily.annual);
    }
  }, [productFamily, selectedPlan]);

  // Parse plan details (format: "qty-planId" or just "planId")
  // In product mode, use selectedPlan; otherwise parse from URL
  const effectivePlan = isProductMode ? selectedPlan : plan;
  const planParts = effectivePlan ? effectivePlan.split("-") : [];
  const productId = planParts.length > 1 ? planParts.slice(1).join("-") : planParts[0];

  // Checkout always buys one unit; every plan includes ten seats (decision,
  // Ben, 2026-09-22), and both checkout routes reject anything else. The
  // leading number in a "qty-planId" link and a ?qty= parameter are ignored
  // rather than rejected, so old bookmarks and marketing links still work.
  const quantity = "1";

  // Handle form data changes
  const handleFormChange = (data, isComplete) => {
    setLeadData(data);
    setFormComplete(isComplete);
    // Store lead data in localStorage for webhook to retrieve customer info
    if (data) {
      localStorage.setItem('iat_lead_data', JSON.stringify({
        ...data,
        timestamp: Date.now(),
        productId,
      }));
    }
  };

  // Handle form validation - check for existing user
  const handleLeadFormValidated = (data, validation) => {
    if (validation.existingUser) {
      // Existing user - redirect to sign in with return URL
      const returnUrl = isProductMode
        ? `${router.pathname}?product=${product}`
        : `${router.pathname}?plan=${plan}`;
      router.push(`/auth/signin?next=${encodeURIComponent(returnUrl)}`);
      return;
    }
  };

  // Handle plan selection change
  const handlePlanChange = (newPlan) => {
    setSelectedPlan(newPlan);
  };

  // Handle "Continue to Payment" - create hosted checkout session and redirect
  const handleContinueToPayment = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const stripePriceId = getStripePriceId(productId);
      const onboardingPath = getOnboardingRoute(productId);

      if (!stripePriceId) {
        throw new Error(`Invalid product: ${productId}`);
      }

      console.log("Creating hosted checkout:", {
        productId,
        quantity,
        stripePriceId,
        onboardingPath,
        isGuest: !auth.user,
        leadEmail: leadData?.email,
      });

      // Use guest or authenticated endpoint
      const endpoint = auth.user ? "stripe-create-checkout-session" : "stripe-guest-checkout";
      const apiFunc = auth.user ? apiRequest : apiRequestNoAuth;

      // Build cancel URL — keep src so attribution survives an abandoned checkout
      // Keep the coupon on the cancel URL too, so an abandoned checkout
      // returns to a page that still knows about the discount.
      const srcParam = (src ? `&src=${encodeURIComponent(src)}` : '') +
        (coupon ? `&coupon=${encodeURIComponent(coupon)}` : '');
      const cancelUrl = isProductMode
        ? `${window.location.origin}/checkout?product=${product}${srcParam}`
        : `${window.location.origin}/checkout?plan=${plan}${srcParam}`;

      const response = await apiFunc(endpoint, "POST", {
        priceIds: [quantity, stripePriceId],
        ui_mode: "hosted",
        successUrl: `${window.location.origin}${onboardingPath}?paid=true&product=${productId}&session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl,
        ...(coupon ? { couponCode: coupon } : {}),
        customerData: leadData,
        metadata: {
          flow_type: "embedded_checkout",
          product_id: productId,
          is_new_user: String(!auth.user),
          ...(src ? { source: src } : {}),
        },
      });

      if (response.url) {
        // The onboarding page must present the buyer's email to fetch the
        // password setup token for this session (api/get-password-token-by-session).
        try {
          if (leadData?.email) sessionStorage.setItem("iat_checkout_email", leadData.email);
        } catch (e) { /* storage unavailable, the emailed link still works */ }
        window.location.href = response.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (err) {
      console.error("Checkout error:", err);
      setError(err.message || "Failed to start checkout. Please try again.");
      setIsSubmitting(false);
    }
  };

  // For authenticated users, go straight to Stripe
  const handleAuthUserCheckout = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const stripePriceId = getStripePriceId(productId);
      const onboardingPath = getOnboardingRoute(productId);

      if (!stripePriceId) {
        throw new Error(`Invalid product: ${productId}`);
      }

      // Build cancel URL — keep src so attribution survives an abandoned checkout
      // Keep the coupon on the cancel URL too, so an abandoned checkout
      // returns to a page that still knows about the discount.
      const srcParam = (src ? `&src=${encodeURIComponent(src)}` : '') +
        (coupon ? `&coupon=${encodeURIComponent(coupon)}` : '');
      const cancelUrl = isProductMode
        ? `${window.location.origin}/checkout?product=${product}${srcParam}`
        : `${window.location.origin}/checkout?plan=${plan}${srcParam}`;

      const response = await apiRequest("stripe-create-checkout-session", "POST", {
        priceIds: [quantity, stripePriceId],
        ui_mode: "hosted",
        successUrl: `${window.location.origin}${onboardingPath}?paid=true&product=${productId}&session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl,
        ...(coupon ? { couponCode: coupon } : {}),
        metadata: {
          flow_type: "embedded_checkout",
          product_id: productId,
          is_new_user: "false",
          ...(src ? { source: src } : {}),
        },
      });

      if (response.url) {
        // The onboarding page must present the buyer's email to fetch the
        // password setup token for this session (api/get-password-token-by-session).
        try {
          if (leadData?.email) sessionStorage.setItem("iat_checkout_email", leadData.email);
        } catch (e) { /* storage unavailable, the emailed link still works */ }
        window.location.href = response.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (err) {
      console.error("Checkout error:", err);
      setError(err.message || "Failed to start checkout. Please try again.");
      setIsSubmitting(false);
    }
  };

  // Show loader only during initial auth check
  if (auth.user === undefined) {
    return <PageLoader />;
  }

  // Determine if form is ready
  const isReady = isProductMode
    ? (selectedPlan && (auth.user || formComplete))
    : (productId && (auth.user || formComplete));

  return (
    <>
      <Meta title="Checkout" />
      <div className="min-h-screen bg-gray-50">
        {/* Logo Header - non-clickable */}
        <div className="bg-white border-b">
          <div className="container mx-auto px-4 max-w-6xl py-4">
            <img src={logo} alt="Insurance Agency Training" className="h-12" />
          </div>
        </div>

        <div className="container mx-auto px-4 max-w-6xl py-8">
          {/* Error display at top for visibility */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <span className="text-red-500 mr-2">&#9888;</span>
                <span className="text-red-700">{error}</span>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Forms */}
            <div className="lg:col-span-2 order-2 lg:order-1">
              <div className="bg-white rounded-lg shadow p-6">
                {/* Guest Users - Show Lead Form */}
                {!auth.user && (
                  <>
                    <h2 className="text-2xl font-bold mb-4">Your Information</h2>
                    <LeadCaptureForm
                      onValidated={handleLeadFormValidated}
                      onFormChange={handleFormChange}
                    />

                    {/* Sign in link for existing users */}
                    <div className="mt-4 text-center">
                      <p className="text-sm text-gray-600">
                        Already have an account?{" "}
                        <button
                          onClick={() => {
                            const returnUrl = isProductMode
                              ? `${router.pathname}?product=${product}`
                              : `${router.pathname}?plan=${plan}`;
                            router.push(`/auth/signin?next=${encodeURIComponent(returnUrl)}`);
                          }}
                          className="text-green-700 hover:text-green-800 font-medium"
                        >
                          Sign in
                        </button>
                      </p>
                    </div>

                    {/* Continue to Payment Button */}
                    <div className="mt-6">
                      <button
                        onClick={handleContinueToPayment}
                        disabled={!isReady || isSubmitting}
                        className={`w-full py-3 px-6 rounded-lg font-semibold text-lg transition-colors ${
                          isReady && !isSubmitting
                            ? "bg-green-600 hover:bg-green-700 text-white"
                            : "bg-gray-300 text-gray-500 cursor-not-allowed"
                        }`}
                      >
                        {isSubmitting ? (
                          <span className="flex items-center justify-center">
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Redirecting to payment...
                          </span>
                        ) : (
                          "Continue to Payment"
                        )}
                      </button>
                      {!formComplete && (
                        <p className="text-sm text-gray-500 text-center mt-2">
                          Please complete all fields above to continue
                        </p>
                      )}
                    </div>
                  </>
                )}

                {/* Authenticated Users - Simple checkout button */}
                {auth.user && (
                  <>
                    <h2 className="text-2xl font-bold mb-4">Complete Your Purchase</h2>
                    <p className="text-gray-600 mb-6">
                      You're signed in as <strong>{auth.user.email}</strong>.
                      Click below to continue to secure payment.
                    </p>

                    <button
                      onClick={handleAuthUserCheckout}
                      disabled={isSubmitting || !isReady}
                      className={`w-full py-3 px-6 rounded-lg font-semibold text-lg transition-colors ${
                        !isSubmitting && isReady
                          ? "bg-green-600 hover:bg-green-700 text-white"
                          : "bg-gray-300 text-gray-500 cursor-not-allowed"
                      }`}
                    >
                      {isSubmitting ? (
                        <span className="flex items-center justify-center">
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Redirecting to payment...
                        </span>
                      ) : (
                        "Continue to Payment"
                      )}
                    </button>

                    {!isReady && !isProductMode && (
                      <p className="text-sm text-red-500 text-center mt-2">
                        No product selected. Please go back and select a plan.
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Right Column - Plan Selection & Summary (shows first on mobile) */}
            <div className="lg:col-span-1 order-1 lg:order-2 space-y-6">
              {/* Billing Selector - Show when in product mode */}
              {isProductMode && (
                <div className="bg-white rounded-lg shadow p-6">
                  <BillingSelector
                    product={product}
                    selectedPlan={selectedPlan}
                    onPlanChange={handlePlanChange}
                  />
                </div>
              )}

              {/* Order Summary */}
              <CheckoutSummary plan={effectivePlan} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default CheckoutPage;
